import { Stack } from '@fluentui/react';
import { comboboxLoader, Loader, LoaderType } from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo } from 'react';

import { usePermissions } from '../../../authz/providers/PermissionsProvider/PermissionsProvider';
import type { S3Object } from '../../../drive/types';
import { useFileProgress } from '../../../drive/useFileProgress';
import type { UseS3ServiceReturn } from '../../../drive/useS3Service';
import { createUploadingObjects, driveUserActions, findActiveOperation, getDrivePermissions } from '../helpers';
import { useDriveOperations, useSearchOperations, useShareOperations, useSorting } from '../hooks';
import { ConnectionStatus } from './ConnectionStatus';
import { ControlsSection } from './ControlsSection';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { DestinationSelectionModal } from './DestinationSelectionModal';
import { DirectoryNameModal } from './DirectoryNameModal';
import { FileNameValidationModal } from './FileNameValidationModal/FileNameValidationModal';
import { FileTable } from './FileTable';
import { GetLinkModal } from './GetLinkModal';
import { NameInputModal } from './NameInputModal';
import { NoAccess } from './NoAccess';
import { ShareModal } from './ShareModal';

interface DriveContentProps {
  activeWorkspaceInitialized: boolean;
  isAutoConnecting: boolean;
  autoConnectionError: string | null;
  s3Service: UseS3ServiceReturn;
  isNavigating: boolean;
  onDirectoryClick: (path: string) => void;
  currentPath: string;
  stsDriveUrl?: string;
  workspaceDriveUrl?: string;
}

export const DriveContent = (props: DriveContentProps) => {
  const {
    activeWorkspaceInitialized,
    isAutoConnecting,
    autoConnectionError,
    s3Service,
    isNavigating,
    onDirectoryClick,
    currentPath,
    stsDriveUrl,
    workspaceDriveUrl,
  } = props;
  const [canListObjects, canReadObjects, canWriteObjects, canDeleteObjects, permissionsLoading] =
    usePermissions(driveUserActions);

  const fileProgress = useFileProgress();

  const { isReader, isWriter, canDownload } = getDrivePermissions([
    canListObjects,
    canReadObjects,
    canWriteObjects,
    canDeleteObjects,
  ]);

  const driveOperations = useDriveOperations({
    s3Service,
    currentDirectoryPath: currentPath,
    stsDriveUrl,
    workspaceDriveUrl,
    onFileUploadStart: (key, filename) => fileProgress.startFileOperation(key, filename, 'upload'),
    onFileUploadComplete: (key) => fileProgress.completeFileOperation(key),
  });
  const shareOperations = useShareOperations();

  const getObjectsWithParentDirectory = (objects: S3Object[], path: string): S3Object[] => {
    const hasParentDir = objects.some((obj) => obj.isParentDirectory);
    if (!path || hasParentDir) {
      return objects;
    }

    return [{ Key: '', isParentDirectory: true }, ...objects];
  };

  const baseObjects = getObjectsWithParentDirectory(s3Service.objects || [], currentPath);

  const { sortedObjects, sortField, sortDirection, handleSort } = useSorting(baseObjects, {
    currentPath,
  });

  const { searchQuery, setSearchQuery, clearSearch, filterObjects } = useSearchOperations();

  const filteredAndSortedObjects = filterObjects(sortedObjects);

  // Add uploading files to the object list so they appear immediately
  const objectsWithProgress = useMemo(
    () => createUploadingObjects(filteredAndSortedObjects, fileProgress.progressEntries),
    [filteredAndSortedObjects, fileProgress.progressEntries]
  );

  useEffect(() => {
    clearSearch();
  }, [currentPath, clearSearch]);

  // Wrap download handler to track progress in file list
  const handleDownloadFileWithProgress = useCallback(
    async (key: string) => {
      const filename = key.split('/').pop() || 'download';

      try {
        // Use pre-signed URL for download (more efficient, no progress tracking)
        const preSignedUrl = await s3Service.generatePresignedUrl(key, 300); // 5 minute expiry

        // Trigger browser download
        const link = document.createElement('a');
        link.href = preSignedUrl;
        link.download = filename;
        link.target = '_blank'; // Open in new tab if needed
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Failed to download file:', error);
      }
    },
    [s3Service]
  );

  // Track upload progress updates
  useEffect(() => {
    if (s3Service.isUploading && s3Service.uploadProgress > 0) {
      const uploadingFile = findActiveOperation(fileProgress.progressEntries, 'upload');
      if (uploadingFile) {
        fileProgress.updateFileProgress(uploadingFile.key, s3Service.uploadProgress);
      }
    }
  }, [s3Service.isUploading, s3Service.uploadProgress, fileProgress]);

  const existingKeys = (objectsWithProgress?.map((obj: S3Object) => obj.Key).filter(Boolean) as string[]) || [];
  const allOperationErrors = [
    ...(s3Service.uploadError ? [{ type: 'upload', message: s3Service.uploadError.message }] : []),
  ];
  const showFileOperations = !permissionsLoading && isReader && activeWorkspaceInitialized && s3Service.isConnected;
  const isFileOperationInProgress = s3Service.isUploading || s3Service.isLoading || isNavigating;

  if (!permissionsLoading && !isReader) return <NoAccess />;

  if (!activeWorkspaceInitialized || isAutoConnecting || autoConnectionError || permissionsLoading) {
    return (
      <ConnectionStatus
        isAutoConnecting={isAutoConnecting}
        autoConnectionError={autoConnectionError}
        activeWorkspaceInitialized={activeWorkspaceInitialized}
      />
    );
  }

  const content = showFileOperations ? (
    <Stack tokens={{ childrenGap: 16 }}>
      <ControlsSection
        onFileUpload={driveOperations.handleFileUpload}
        onCreateDirectory={driveOperations.handleCreateDirectory}
        isUploading={driveOperations.isUploadingFiles}
        operationErrors={allOperationErrors}
        canWrite={isWriter}
        canRead={isReader}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onShareDirectory={() => shareOperations.handleShareAccessDirectory()}
      />
      <Stack styles={{ root: { position: 'relative', marginTop: '0 !important', top: 10 } }}>
        <Loader
          type={LoaderType.progressIndicator}
          styles={comboboxLoader}
          hidden={!isFileOperationInProgress}
          style={{ position: 'absolute', left: 0, top: 0, right: 0 }}
        />
      </Stack>
      <FileTable
        objects={objectsWithProgress}
        isLoading={s3Service.isLoading}
        error={s3Service.error}
        onDeleteFile={driveOperations.handleDeleteRequest}
        isWriter={isWriter}
        currentPath={currentPath}
        onDirectoryClick={onDirectoryClick}
        onCopyFile={driveOperations.handleCopyFile}
        onMoveFile={driveOperations.handleMoveFile}
        onRenameFile={driveOperations.handleRenameRequest}
        onDownloadFile={handleDownloadFileWithProgress}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        onCopyLinkItem={(item) => shareOperations.handleCopyLinkItem(item, currentPath)}
        onShareAccessItem={(item) => shareOperations.handleShareAccessItem(item, currentPath)}
        fileProgressMap={fileProgress.progressEntries}
        canDownload={canDownload}
      />
    </Stack>
  ) : null;

  return (
    <>
      {content}

      <FileNameValidationModal
        isOpen={driveOperations.validationModalOpen}
        onClose={driveOperations.handleValidationModalClose}
        onConfirm={driveOperations.handleValidationModalConfirm}
        invalidFiles={driveOperations.invalidFileResults}
        existingKeys={existingKeys}
      />

      <DirectoryNameModal
        isOpen={driveOperations.directoryModalOpen}
        onClose={driveOperations.handleDirectoryModalClose}
        onConfirm={driveOperations.handleDirectoryModalConfirm}
        existingDirectories={driveOperations.existingDirectories}
      />

      <DeleteConfirmationModal
        isOpen={driveOperations.deleteConfirmationOpen}
        onClose={driveOperations.handleDeleteConfirmationClose}
        onConfirm={driveOperations.handleDeleteConfirmationConfirm}
        itemName={driveOperations.itemToDelete?.name || ''}
        isDirectory={driveOperations.itemToDelete?.isDirectory || false}
      />

      <DestinationSelectionModal
        isOpen={driveOperations.destinationModalOpen}
        onClose={driveOperations.handleDestinationModalClose}
        onConfirm={driveOperations.handleDestinationModalConfirm}
        allObjects={driveOperations.allWorkspaceObjects}
        sourceItemName={driveOperations.copyMoveOperation?.name || ''}
        sourceKey={driveOperations.copyMoveOperation?.key || ''}
        operation={driveOperations.copyMoveOperation?.operation || 'copy'}
        s3Service={{
          createDirectory: s3Service.createDirectory,
          listAllObjects: s3Service.listAllObjects,
        }}
        onObjectsRefresh={driveOperations.refreshAllObjects}
        stsDriveUrl={stsDriveUrl}
        workspaceDriveUrl={workspaceDriveUrl}
        isOperationInProgress={driveOperations.isOperationInProgress}
      />

      <NameInputModal
        isOpen={driveOperations.renameModalOpen}
        onClose={driveOperations.handleRenameModalClose}
        onConfirm={driveOperations.handleRenameModalConfirm}
        title={driveOperations.itemToRename?.isDirectory ? 'Rename Directory' : 'Rename File'}
        confirmButtonText="Rename"
        initialValue={driveOperations.itemToRename?.name || ''}
        existingNames={existingKeys}
        generateUniqueName={driveOperations.generateUniqueName}
        currentItemName={driveOperations.itemToRename?.name}
      />

      <ShareModal
        isOpen={shareOperations.shareAccessModal.isOpen}
        onClose={shareOperations.shareAccessModal.close}
        shareUrl={shareOperations.shareAccessModal.data?.shareUrl || ''}
        isDirectory={shareOperations.shareAccessModal.data?.isDirectory}
        itemKey={shareOperations.shareAccessModal.data?.itemKey}
      />

      <GetLinkModal
        isOpen={shareOperations.copyLinkModal.isOpen}
        onClose={shareOperations.copyLinkModal.close}
        itemName={shareOperations.copyLinkModal.data?.itemName}
        isDirectory={shareOperations.copyLinkModal.data?.isDirectory}
        itemKey={shareOperations.copyLinkModal.data?.itemKey}
        s3ReferenceUrl={shareOperations.copyLinkModal.data?.s3ReferenceUrl}
        onGeneratePresignedUrl={canDownload ? s3Service.generatePresignedUrl : undefined}
      />
    </>
  );
};
